// WalkthroughContext.js - Manages app walkthrough/tour state
import React, { createContext, useContext, useState, useCallback } from 'react';

const WalkthroughContext = createContext();

export const useWalkthrough = () => {
  const context = useContext(WalkthroughContext);
  if (!context) {
    throw new Error('useWalkthrough must be used within a WalkthroughProvider');
  }
  return context;
};

export const WalkthroughProvider = ({ children }) => {
  const [shouldStartWalkthrough, setShouldStartWalkthrough] = useState(false);
  const [walkthroughCompleted, setWalkthroughCompleted] = useState(false);
  const [isWalkthroughActive, setIsWalkthroughActive] = useState(false);

  // Called when onboarding finishes to trigger walkthrough
  const triggerWalkthrough = useCallback(() => {
    setShouldStartWalkthrough(true);
  }, []);

  // Called when walkthrough actually starts
  const onWalkthroughStart = useCallback(() => {
    setIsWalkthroughActive(true);
    setShouldStartWalkthrough(false);
  }, []);

  // Called when walkthrough finishes
  const onWalkthroughComplete = useCallback(() => {
    setIsWalkthroughActive(false);
    setWalkthroughCompleted(true);
  }, []);

  // Skip walkthrough
  const skipWalkthrough = useCallback(() => {
    setShouldStartWalkthrough(false);
    setWalkthroughCompleted(true);
  }, []);

  // Abort without recording the tour as done (steps never registered, target
  // vanished, screen left). Nothing re-triggers it this session;
  // walkthroughCompleted is in-memory, so it retries only when onboarding
  // triggers it again.
  const abortWalkthrough = useCallback(() => {
    setShouldStartWalkthrough(false);
    setIsWalkthroughActive(false);
  }, []);

  // Reset (for testing)
  const resetWalkthrough = useCallback(() => {
    setShouldStartWalkthrough(false);
    setWalkthroughCompleted(false);
    setIsWalkthroughActive(false);
  }, []);

  const value = {
    shouldStartWalkthrough,
    walkthroughCompleted,
    isWalkthroughActive,
    triggerWalkthrough,
    onWalkthroughStart,
    onWalkthroughComplete,
    skipWalkthrough,
    abortWalkthrough,
    resetWalkthrough,
  };

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
    </WalkthroughContext.Provider>
  );
};

export default WalkthroughContext;
